<template>

    <div class="flex flex-column w-full" style="color: var(--theme-font-color);">
        <span class="subtitle p-0 m-0 w-full flex flex-row gap-2 align-items-center">
            History
            <div v-if="license.history.history_content.length >= license.history.history_max" class="h-full flex align-items-center" v-tooltip.right="{ value: `The history list displays a maximum of ${license.history.history_max} operations. Your history might contain more than that, thus you will not see some of the older ones.` }">
                <SvgInfoCircle className="pluscircle-svg" color="var(--theme-font-color)"/>
            </div>
        </span>
        <DataTable v-if="sortedHistory" class="mt-2" :value="sortedHistory" size="small" :row-class="() => 'row'" tableStyle="min-width: 100%" stripedRows scrollable scrollHeight="flex" style="background-color: var(--table-row-color);" :style="`max-height: ${windowWidth>575 ? '18' : '10'}rem`">
            <template #empty>
                <div class="w-full flex flex-column justify-content-center align-items-center py-3 px-8 text-center" style="background-color: var(--table-row-color)">
                    <span>
                        No history found.
                    </span>
                </div>
            </template>
            
            <Column field="dateTime" header="Date/Time" style="width: 35%; min-width: 4rem; max-width: 16rem">
                <template #body="slotProps">
                    <span class="overflow-hidden">{{ format(new Date(slotProps.data.event_timestamp*1000), 'MMM do, yyyy, HH:mm') }}</span>
                </template>
            </Column>
            <Column field="user" header="User" style="min-width: 2rem; max-width: 7rem">
                <template #body="slotProps">
                    <div v-if="slotProps.data.event_user">
                        <Avatar v-if="user.id===slotProps.data.event_user.id" class="avatar" size="small" shape="circle" v-tooltip.top="{ value: `${slotProps.data.event_user.first_name} ${slotProps.data.event_user.last_name} (${slotProps.data.event_user.email})`}">
                            <SvgUserMe className="avatar-me-svg" color="var(--theme-font-color)"/>
                        </Avatar>
                        <Avatar v-else class="avatar" :label="getAvatarLabelFromFirstNameAndLastName(slotProps.data.event_user.first_name, slotProps.data.event_user.last_name)" size="small" shape="circle" v-tooltip.top="{ value: `${slotProps.data.event_user.email != '' ? `${slotProps.data.event_user.first_name} ${slotProps.data.event_user.last_name} (${slotProps.data.event_user.email})` : 'Deleted Account'}`}"/>
                    </div>
                </template>
            </Column>
            <Column field="status" header="Status" style="width: 45%;max-width: 20rem">
                <template #body="slotProps"> <!-- ATTACH => Available, DETACH => /, ACTIV => In Use (+ info), DEACTIV => Available, FORCE_RELEASE => Force Release / Available -->
                    <div class="tag-small overflow-hidden">
                        <div v-if="slotProps.data.event_operation === 'TRANSFER'" v-tooltip.right="{ value: `${slotProps.data.event_user.id === user.id ? 'You' : slotProps.data.event_user.email == '' ? 'Someone (Deleted Account)' : `${slotProps.data.event_user.first_name} ${slotProps.data.event_user.last_name}`} transferred the ownership of the license to another user.`, showDelay: 500 }">
                            <Tag value="Transferred" class="tag-added-removed" rounded>
                                <span class="tag-label">Transferred</span>
                            </Tag>
                        </div>
                        <div v-else-if="slotProps.data.event_operation === 'DETACH'" v-tooltip.right="{ value: `${slotProps.data.event_user.id === user.id ? 'You' : slotProps.data.event_user.email == '' ? 'Someone (Deleted Account)' : `${slotProps.data.event_user.first_name} ${slotProps.data.event_user.last_name}`} removed the license that became free-to-attach.`, showDelay: 500 }">
                            <Tag value="Removed" class="tag-added-removed" rounded>
                                <span class="tag-label">Removed</span>
                            </Tag>
                        </div>
                        <div v-else-if="slotProps.data.event_operation === 'ATTACH'" v-tooltip.right="{ value: `${slotProps.data.event_user.id === user.id ? 'You' : slotProps.data.event_user.email == '' ? 'Someone (Deleted Account)' : `${slotProps.data.event_user.first_name} ${slotProps.data.event_user.last_name}`} attached the license to ${slotProps.data.event_user.id === user.id ? 'your' : 'their'} account.`, showDelay: 500 }">
                            <Tag value="Available" class="tag-added-removed" rounded> <!-- style="background-color: var(--theme-font-color);" > -->
                                <span class="tag-label">Added</span>
                            </Tag>
                        </div>
                        <div v-else-if="slotProps.data.event_operation === 'DEACTIV' && slotProps.data.event_user" v-tooltip.right="{ value: `${slotProps.data.event_user.id === user.id ? 'You' : slotProps.data.event_user.email == '' ? 'Someone (Deleted Account)' : `${slotProps.data.event_user.first_name} ${slotProps.data.event_user.last_name}`} stopped the running license.`, showDelay: 500 }">
                            <Tag value="Available" class="tag-available" rounded>
                                <span class="tag-label">Available</span>
                            </Tag>
                        </div>
                        <div v-else-if="slotProps.data.event_operation === 'DEACTIV' && !slotProps.data.event_user" v-tooltip.right="{ value: `The license activation got stopped in the old activation system.`, showDelay: 500 }">
                            <Tag value="Available" class="tag-available" rounded>
                                <span class="tag-label">Available</span>
                            </Tag>
                        </div>
                        <div v-else-if="(slotProps.data.event_operation === 'ACTIV' || slotProps.data.event_operation === 'REACTIV') && slotProps.data.event_user" v-tooltip.right="{ value: `${slotProps.data.event_user.id === user.id ? 'You' : slotProps.data.event_user.email == '' ? 'Someone (Deleted Account)' : `${slotProps.data.event_user.first_name} ${slotProps.data.event_user.last_name}`} ${slotProps.data.event_operation === 'REACTIV' ? 'reactivated' : 'started using'} the license.`, showDelay: 500 }">
                            <Tag value="In Use" class="is-active-user tag-inuse" rounded>
                                <div class="tag-container flex flex-row align-items-center justify-content-center">
                                    <span class="tag-label" style="white-space: nowrap;">In Use</span>
                                    <span v-if="slotProps.data.event_device && slotProps.data.event_device.label" class="tag-sublabel potentially-long-label flex flex-row gap-0">
                                        (
                                            <span style="transform: translateY(0.03rem);">{{ slotProps.data.event_device.label }}</span>
                                        )
                                    </span>
                                </div>
                            </Tag>
                        </div>
                        <div v-else-if="slotProps.data.event_operation === 'ACTIV' && !slotProps.data.event_user" v-tooltip.right="{ value: `The license got activated in the old activation system.`, showDelay: 500 }">
                            <Tag value="In Use" class="is-active-user tag-inuseold" rounded>
                                <div class="tag-container flex flex-row align-items-center justify-content-center">
                                    <span class="tag-label" style="white-space: nowrap;">In Use</span>
                                    <span class="tag-sublabel potentially-long-label flex flex-row gap-0">
                                        (
                                        <span v-if="slotProps.data.event_device && slotProps.data.event_device.mac" style="transform: translateY(0.03rem);">{{ slotProps.data.event_device.mac }}</span>
                                        <span v-else style="transform: translateY(0.03rem); font-style: italic;">Unknown MAC address</span>
                                        )
                                    </span>
                                </div>
                            </Tag>
                        </div>
                        <div class="flex flex-row align-items-center" v-else-if="slotProps.data.event_operation === 'FORCE_RELEASE'" v-tooltip.right="{ value: `${slotProps.data.event_user.id === user.id ? 'You' : slotProps.data.event_user.email == '' ? 'Someone (Deleted Account)' : `${slotProps.data.event_user.first_name} ${slotProps.data.event_user.last_name}`} forced the release of the running license.`, showDelay: 500 }">
                            <Tag value="Force Released" class="tag-force-released" rounded>
                                <div class="tag-container flex flex-row align-items-center justify-content-center">
                                    <span class="tag-label text-center">Force Released</span>
                                </div>
                            </Tag>
                            <SvgArrowRight className="history-arrow-svg" class="mx-2" color="var(--theme-font-color)"/>
                            <Tag value="Available" class="tag-available" rounded>
                                <span class="tag-label">Available</span>
                            </Tag>
                        </div>
                        <div v-if="slotProps.data.event_operation === 'MIGRATION'" v-tooltip.right="{ value: `${slotProps.data.event_user.id === user.id ? 'You' : slotProps.data.event_user.email == '' ? 'Someone (Deleted Account)' : `${slotProps.data.event_user.first_name} ${slotProps.data.event_user.last_name}`} migrated the activation to the new system.`, showDelay: 500 }">
                            <Tag value="Removed" class="tag-added-removed" rounded>
                                <span class="tag-label">Migrated</span>
                            </Tag>
                        </div>
                    </div>
                </template>
            </Column>
        </DataTable>
  </div>
</template>

<script setup>
// --- IMPORTS ---
import { ref, toRefs, computed, onMounted, onBeforeUnmount } from 'vue';
// PrimeVue
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Avatar from 'primevue/avatar'
import Tag from 'primevue/tag'
// vue
import SvgUserMe from '@/components/SVGs/SvgUserMe.vue'
import SvgArrowRight from '@/components/SVGs/SvgArrowRight.vue'
import SvgInfoCircle from '@/components/SVGs/SvgInfoCircle.vue';
// Others
import AvatarsUtils from '@/composables/AvatarsUtils'
import { format } from 'date-fns';
import { useStore } from 'vuex';

const store = useStore()
const { getAvatarLabelFromFirstNameAndLastName } = AvatarsUtils()

// --- PROPS ---
const props = defineProps({
    license: Object
})
const { license } = toRefs(props)

// --- User info ---
const user = computed(() => store.state.user.user)

// --- Stored history ---
const sortedHistory = computed(() => license.value.history.history_content.sort((a,b) => Number(a.event_id)<Number(b.event_id)))

// -- WINDOW WIDTH TRACKING (for responsiveness of ui) --
const windowWidth = ref(window.innerWidth);
const updateWindowWidth = () => {
    windowWidth.value = window.innerWidth;
};
onMounted(() => {
  window.addEventListener('resize', updateWindowWidth);
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateWindowWidth);
});
</script>

<style scoped>
/* Header */
.p-datatable :deep(.p-datatable-thead th) {
    background-color: var(--table-header-color);
    border-color: transparent;
}
/* Rows */
.p-datatable :deep(.row) {
    background-color: var(--table-row-color);
}
</style>